import React from 'react';
import { Star, Download, Eye, FileText, File, AlignLeft, Calendar, Building, Gavel } from 'lucide-react';

interface SearchResultItemProps {
  result: any;
  onPreview: (result: any) => void;
  onDownload: (result: any) => void;
  onToggleFavorite: (result: any) => void;
  isFavorite: boolean;
}

export const SearchResultItem: React.FC<SearchResultItemProps> = ({ 
  result, 
  onPreview, 
  onDownload, 
  onToggleFavorite, 
  isFavorite 
}) => {
  const getFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'docx':
      case 'doc':
        return <File className="w-5 h-5 text-blue-500" />;
      case 'txt':
        return <AlignLeft className="w-5 h-5 text-gray-500" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            {getFileTypeIcon(result.file_type)}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer truncate max-w-96" title={result.title}>
                {result.title}
              </h3>
              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center space-x-1">
                  <Building className="w-4 h-4" />
                  <span className="truncate max-w-32" title={result.publisher || 'N/A'}>{result.publisher || 'N/A'}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Gavel className="w-4 h-4" />
                  <span className="truncate max-w-32" title={result.court || 'N/A'}>{result.court || 'N/A'}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(result.date_of_document)}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Summary */}
          {result.summary && (
            <p className="text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
              {result.summary}
            </p>
          )}

          {/* Highlights */}
          {result.highlights && Object.keys(result.highlights).length > 0 && (
            <div className="mb-3">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Relevant excerpts:</p>
              {Object.entries(result.highlights).map(([field, highlights]) => (
                <div key={field} className="mb-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                    {field}:
                  </span>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    {Array.isArray(highlights) && highlights.map((highlight: string, index: number) => (
                      <span
                        key={index}
                        dangerouslySetInnerHTML={{ __html: highlight }}
                        className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* File Info */}
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span className="truncate max-w-48" title={result.file_name}>File: {result.file_name}</span>
            <span className="whitespace-nowrap">Size: {formatFileSize(result.file_size)}</span>
            <span className="whitespace-nowrap">Type: {result.document_type}</span>
            {result.score && (
              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap">
                Score: {result.score.toFixed(2)}
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2 ml-4">
          <button
            onClick={() => onPreview(result)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title="Preview Document"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => onDownload(result)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
            title="Download Document"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={() => onToggleFavorite(result)}
            className={`p-2 rounded-lg transition-colors ${
              isFavorite
                ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                : 'text-gray-600 dark:text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
            }`}
            title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
          >
            <Star className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
};
